import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  IAuthResponse,
  IPublicBusinessConfig,
} from '@g99-product-web-ui/shared-interface-lib';
import {
  AnalyticsProxyService,
  CryptoHelperServiceService,
  HTTP_HEADERS_FOR_NON_AUTH_API,
  HttpRequestOptions,
  HttpService,
  LocalStorageService,
  ServiceType,
  SessionTrackingService,
  SharedBusinessService,
  SharedUserService,
  SubjectManageService,
} from '@g99-product-web-ui/shared-service';
import {
  API_ENDPOINTS,
  LOCAL_STORAGE_KEYS,
  ROUTE_CONSTS,
} from '@g99-product-web-ui/shared-util-lib';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { IChangePasswordRequest } from '../interface/IChange-password.interface';
import { IStoredCredentials } from '../interface/ICredentials.interface';
import { IForgotPasswordPayload } from '../interface/IForgot-password.interface';
import {
  IUserBusinessInfo,
  IUserCredentials,
} from '../interface/ISignIn.interface';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly _storageKey = 'auth_credentials';

  constructor(
    private readonly _httpService: HttpService,
    private readonly _cryptoHelper: CryptoHelperServiceService,
    private readonly _localStorageService: LocalStorageService,
    private readonly _sharedBusinessService: SharedBusinessService,
    private readonly _subjectManagerService: SubjectManageService,
    private readonly _router: Router,
    private readonly _sharedUserService: SharedUserService,
    private readonly _analyticsService: AnalyticsProxyService,
    private readonly _sessionTrackingService: SessionTrackingService
  ) {}

  /**
   * Fetches the list of available businesses associated with a given email.
   * @param email - The email address to search for associated businesses.
   * @returns An Observable that emits an array of IUserBusinessInfo objects.
   */
  public getAvailableBusinesses(
    email: string
  ): Observable<IUserBusinessInfo[]> {
    const url = `${API_ENDPOINTS.AUTH_ENDPOINTS.getUserByEmail}?email=${encodeURIComponent(email)}`;
    return this._httpService.get<IUserBusinessInfo[]>(
      url,
      HTTP_HEADERS_FOR_NON_AUTH_API,
      ServiceType.AUTH
    );
  }

  /**
   * Authenticates a user with credentials and an optional business ID.
   * If a business ID is provided, retrieves business info before login.
   * @param formData - The user credentials and optional business ID.
   * @returns An Observable that emits an IAuthResponse object or null if no business ID is provided.
   */
  public loginWithCredsAndBid(
    formData: IUserCredentials
  ): Observable<IAuthResponse | null> {
    const url = API_ENDPOINTS.AUTH_ENDPOINTS.newAuthLogin;
    if (formData?.businessId) {
      return this._httpService
        .post<
          IAuthResponse,
          IUserCredentials
        >(url, formData, HTTP_HEADERS_FOR_NON_AUTH_API, ServiceType.AUTH)
        .pipe(
          switchMap((authInfo: IAuthResponse) => {
            const expirationTime = Date.now() + authInfo.expiresIn * 1000;
            this._localStorageService.storeItem('currentUser', authInfo);
            this._localStorageService.storeItem(
              LOCAL_STORAGE_KEYS.TOKEN_EXPIRATION,
              expirationTime
            );

            const businessId = authInfo.businessId
              ? Number(authInfo.businessId)
              : null;

            if (businessId) {
              this._localStorageService.storeItem(
                LOCAL_STORAGE_KEYS.BUSINESS_INFO,
                {
                  id: businessId,
                }
              );

              return this._sharedBusinessService
                .fetchBusinessProfile(businessId)
                .pipe(
                  tap((businessInfo: IPublicBusinessConfig) => {
                    this._localStorageService.storeItem(
                      'businessInfo',
                      businessInfo
                    );
                    // Emit userSubject only after businessInfo is ready so the
                    // layout mounts with a valid X-TenantID from the start.
                    this._subjectManagerService.userSubject.next(authInfo);
                    this._subjectManagerService.businessSubject.next(
                      businessInfo
                    );
                  }),
                  tap(() => {
                    this.fetchBusinessConfig(businessId);
                    this._fetchUserRolesAndStoreToLocalStorage(authInfo.id);
                  }),
                  map(() => authInfo),
                  catchError((err: HttpErrorResponse) => {
                    // On 403 the auth interceptor redirects to change-password.
                    // Do NOT emit userSubject — the layout must not mount and
                    // make further API calls while the redirect is in progress.
                    if (err.status === 403) {
                      return EMPTY;
                    }
                    // For other errors still log the user in.
                    this._subjectManagerService.userSubject.next(authInfo);
                    return of(authInfo);
                  })
                );
            } else {
              this._subjectManagerService.userSubject.next(authInfo);
              return of(authInfo);
            }
          }),
          catchError((err) => throwError(() => err))
        );
    } else {
      return of(null);
    }
  }

  /**
   * Stores user credentials securely in local storage.
   * @param credentials - An object containing email and password.
   */
  public storeCredentials(credentials: {
    email: string;
    password?: string;
    isSwitchBusiness?: boolean;
    refreshToken?: string;
  }): void {
    const encryptedData = this._cryptoHelper.encrypt(credentials);
    this._localStorageService.storeItem(this._storageKey, encryptedData);
  }

  /**
   * Retrieves stored user credentials from local storage.
   * @returns The decrypted credentials as an object or null if not found.
   */
  public retrieveCredentials(): IStoredCredentials | null {
    const encryptedData = this._localStorageService.readItem<string>(
      this._storageKey
    );

    if (!encryptedData) {
      return null;
    }

    try {
      const decryptedData =
        this._cryptoHelper.decrypt<IStoredCredentials>(encryptedData);

      // Basic validation — ensures valid shape
      if (decryptedData && typeof decryptedData.email === 'string') {
        return decryptedData;
      }

      return null;
    } catch (error) {
      console.error('Error decrypting auth credentials:', error);
      return null;
    }
  }

  /**
   * Clears stored user credentials from local storage.
   */
  public clearCredentials(): void {
    this._localStorageService.removeItem(this._storageKey);
  }

  /**
   * Redirects the user to the appropriate page after login based on session data.
   * If MFA is required, stores the return URL for post-MFA redirect.
   * @param authResponse - The authentication response containing session details.
   */
  public postLoginRedirection(authResponse: IAuthResponse): void {
    if (authResponse.mfaForced) {
      this._localStorageService.storeItem('tempSessionInfo', authResponse);
      this._router.navigate(['mfa-setup']);
    } else if (authResponse.session && authResponse.challengeName) {
      this._localStorageService.storeItem('tempSessionInfo', authResponse);
      this._router.navigate(['mfa-verify']);
    } else {
      this._handleMfaSuccess(authResponse);
    }
  }

  /**
   * Authenticates a support user using a token.
   * @param token - The support login token.
   * @returns An Observable that emits an IAuthResponse object.
   */
  public supportLogin(token: string): Observable<IAuthResponse> {
    const apiUrl = '/auth/support?t=' + token;
    return this._httpService.get(apiUrl, HTTP_HEADERS_FOR_NON_AUTH_API);
  }

  /**
   * Changes the user's password.
   * @param formData - The form data containing the current and new password.
   * @returns An Observable that emits the response from the API.
   */
  public changePassword(formData: IChangePasswordRequest) {
    const url = API_ENDPOINTS.AUTH_ENDPOINTS.changePassword;
    return this._httpService.post(
      url,
      formData,
      {} as HttpRequestOptions,
      ServiceType.DEFAULT
    );
  }

  public logout(payload?: {
    refreshToken?: string;
    accessToken?: string;
    idToken?: string;
  }): Observable<unknown> {
    const url = API_ENDPOINTS.AUTH_ENDPOINTS.logout;
    return this._httpService.post(
      url,
      payload || {},
      {} as HttpRequestOptions,
      ServiceType.AUTH
    );
  }

  /**
   * Requests a confirmation code to reset the user's password.
   * This confirmation code verifies the user's identity and is required to complete the password reset process.
   *
   * @param userName - The username of the account for which the password reset confirmation code is requested.
   * @returns An Observable that emits the response from the API containing the confirmation code.
   */
  public getConfirmationCodeForPasswordReset(userName: string) {
    const url =
      API_ENDPOINTS.PUBLIC_V2_ENDPOINTS.getPasswordResetConfirmationCode(
        userName
      );
    return this._httpService.get(
      url,
      HTTP_HEADERS_FOR_NON_AUTH_API,
      ServiceType.PUBLIC
    );
  }

  /**
   * Resets the user's password using the provided details.
   * This method is typically used when a user has forgotten their password
   * and needs to set a new one after identity verification.
   *
   * @param payload - An object containing the necessary details such as username,
   *                  new password, and confirmation code required to reset the password.
   * @returns An Observable emitting the API response indicating the success or failure of the password reset process.
   */
  public forgotPassword(payload: IForgotPasswordPayload) {
    const url = API_ENDPOINTS.PUBLIC_V2_ENDPOINTS.forgotPassword;
    return this._httpService.put(
      url,
      payload,
      HTTP_HEADERS_FOR_NON_AUTH_API,
      ServiceType.PUBLIC
    );
  }

  public fetchBusinessConfig(bid?: number): void {
    if (bid) {
      // Fetches + caches config and re-emits businessSubject so the nav rebuilds
      // with the current flags (on login the nav is built before config loads).
      this._sharedBusinessService.refreshBusinessConfig(bid);
    }
  }

  /**
   * Fetches the user roles from the API and updates the user's stored info
   * with the new roles. If the user's info is not available, it logs an error.
   * @param userId The ID of the user for whom the roles are to be fetched.
   */
  private _fetchUserRolesAndStoreToLocalStorage(userId: number): void {
    this._sharedUserService.getUserData(userId).subscribe({
      next: (userData) => {
        if (userData?.roles) {
          const userInfo = this._localStorageService.readItem<IAuthResponse>(
            LOCAL_STORAGE_KEYS.CURRENT_USER
          );
          if (userInfo) {
            userInfo.updatedRole = userData.roles;
            this._localStorageService.storeItem(
              LOCAL_STORAGE_KEYS.CURRENT_USER,
              userInfo
            );
            this._subjectManagerService.userSubject.next(userInfo);
          }
        }
      },
    });
  }

  // In your MFA verify component after successful verification
  private _handleMfaSuccess(authResponse: IAuthResponse): void {
    // Update the auth response in local storage first so analytics can see it
    this._localStorageService.storeItem(
      LOCAL_STORAGE_KEYS.CURRENT_USER,
      authResponse
    );
    this._localStorageService.storeItem(
      LOCAL_STORAGE_KEYS.SESSION_START_TIME,
      Date.now()
    );
    this._subjectManagerService.userSubject.next(authResponse);

    try {
      this._analyticsService.initHeartbeat();
      this._analyticsService.initPageTracking();
      this._sessionTrackingService.start();
    } catch (e) {
      console.error('Analytics initialization failed', e);
    }

    // Remove temp session info
    this._localStorageService.removeItem('tempSessionInfo');

    // A forced password change takes priority over any return URL — the user's
    // tokens are already stored above, so the change-password route (guarded
    // by AuthGuard) is reachable without bouncing back to login.
    if (authResponse.changePasswordForced) {
      this._router.navigate([
        `${ROUTE_CONSTS.USERS.base}/${ROUTE_CONSTS.USERS.changePassword}`,
      ]);
      return;
    }

    // Check for return URL
    const returnUrl = this._localStorageService.readItem<string>(
      LOCAL_STORAGE_KEYS.RETURN_URL
    );

    if (returnUrl && this._subjectManagerService.isValidReturnUrl(returnUrl)) {
      this._router.navigateByUrl(returnUrl);
    } else {
      this._router.navigate([ROUTE_CONSTS.INBOX.base]);
    }
    this._localStorageService.removeItem(LOCAL_STORAGE_KEYS.RETURN_URL);
  }
}
